import React, { useState, useEffect } from "react";
import api from "../lib/api";
import {
  Save, Mail, Key, Bell, TestTube, CheckCircle, AlertTriangle,
  Database, Download, Trash2, Play, RefreshCw, Clock, Archive,
  Upload, RotateCcw, Palette, Image as ImageIcon, Trash, SlidersHorizontal, Hash, Eye,
} from "lucide-react";
import { useConfirm } from "../contexts/ConfirmContext";
import { useBranding } from "../contexts/BrandingContext";

export default function SettingsPage() {
  const [tab, setTab] = useState("email");

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Einstellungen</h1>
        <p className="text-sm text-muted-foreground mt-1">System-Konfiguration</p>
      </div>

      <div className="flex border-b border-border mb-6">
        {[
          { key: "email", label: "E-Mail", icon: Bell },
          { key: "branding", label: "Branding", icon: Palette },
          { key: "numbering", label: "Kundennummern", icon: SlidersHorizontal },
          { key: "backup", label: "Backups", icon: Database },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            data-testid={`settings-tab-${key}`}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "email" && <EmailSettings />}
      {tab === "branding" && <BrandingSettings />}
      {tab === "numbering" && <NumberingSettings />}
      {tab === "backup" && <BackupSettings />}
    </div>
  );
}

// ── Email Settings (existing) ────────────────────────────────────────
function EmailSettings() {
  const [form, setForm] = useState({ sender_email: "", sender_name: "DATON Notifications", notifications_enabled: false, resend_api_key: "" });
  const [hasKey, setHasKey] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get("/settings").then(r => {
      setForm({ sender_email: r.data.sender_email || "", sender_name: r.data.sender_name || "DATON Notifications", notifications_enabled: r.data.notifications_enabled || false, resend_api_key: "" });
      setHasKey(r.data.has_api_key || false);
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      const payload = { sender_email: form.sender_email, sender_name: form.sender_name, notifications_enabled: form.notifications_enabled };
      if (form.resend_api_key) payload.resend_api_key = form.resend_api_key;
      await api.put("/settings", payload);
      setHasKey(hasKey || !!form.resend_api_key);
      setForm(f => ({ ...f, resend_api_key: "" }));
      setMsg({ type: "success", text: "Einstellungen gespeichert" });
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Fehler beim Speichern" });
    } finally { setSaving(false); }
  };

  const sendTest = async (e) => {
    e.preventDefault();
    setTesting(true); setMsg(null);
    try {
      const r = await api.post("/settings/test-email", { email: testEmail });
      setMsg({ type: "success", text: r.data.message });
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Test fehlgeschlagen" });
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={save} className="space-y-4">
        <div className="bg-card border border-border p-6">
          <div className="flex items-center gap-2 mb-5">
            <Bell size={16} className="text-muted-foreground" />
            <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">E-Mail-Benachrichtigungen</h2>
          </div>

          <label className="flex items-center gap-3 mb-5 cursor-pointer">
            <div className={`w-11 h-6 relative transition-colors ${form.notifications_enabled ? "bg-primary" : "bg-border"} rounded-full`}
              onClick={() => setForm(f => ({ ...f, notifications_enabled: !f.notifications_enabled }))}>
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.notifications_enabled ? "translate-x-5" : ""}`} />
            </div>
            <div>
              <p className="text-sm font-semibold">Benachrichtigungen aktivieren</p>
              <p className="text-xs text-muted-foreground">E-Mail bei Ticket-Zuweisung senden</p>
            </div>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5 flex items-center gap-1.5"><Key size={11} /> Resend API-Key</label>
              {hasKey && !showKey ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-3 py-2.5 bg-background border border-input text-sm font-mono text-muted-foreground">re_••••••••••••••••••••••••</div>
                  <button type="button" onClick={() => setShowKey(true)} className="px-3 py-2.5 bg-secondary text-xs font-semibold border border-input">Ersetzen</button>
                </div>
              ) : (
                <input data-testid="settings-resend-key" type="password" value={form.resend_api_key}
                  onChange={e => setForm(f => ({ ...f, resend_api_key: e.target.value }))}
                  placeholder="re_xxxxxxxxxxxxxxxxxx"
                  className="w-full px-3 py-2.5 text-sm font-mono bg-background border border-input focus:border-primary focus:outline-none" />
              )}
              <p className="text-xs text-muted-foreground mt-1">Kostenlos unter <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-primary hover:underline">resend.com</a> erstellen</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5 flex items-center gap-1.5"><Mail size={11} /> Absender E-Mail</label>
              <input data-testid="settings-sender-email" type="email" value={form.sender_email}
                onChange={e => setForm(f => ({ ...f, sender_email: e.target.value }))}
                placeholder="noreply@ihrefirma.ch"
                className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
              <p className="text-xs text-muted-foreground mt-1">Muss bei Resend verifiziert sein</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Absender Name</label>
              <input data-testid="settings-sender-name" type="text" value={form.sender_name}
                onChange={e => setForm(f => ({ ...f, sender_name: e.target.value }))}
                placeholder="DATON Notifications"
                className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
            </div>
          </div>
        </div>

        {msg && (
          <div className={`flex items-center gap-2 px-4 py-3 text-sm border ${msg.type === "success" ? "border-green-300 bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400 dark:border-green-900" : "border-destructive/30 bg-destructive/10 text-destructive"}`}>
            {msg.type === "success" ? <CheckCircle size={15} /> : <AlertTriangle size={15} />}{msg.text}
          </div>
        )}

        <button data-testid="save-settings-button" type="submit" disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50">
          <Save size={15} /> {saving ? "Speichern..." : "Einstellungen speichern"}
        </button>
      </form>

      {hasKey && (
        <div className="bg-card border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <TestTube size={16} className="text-muted-foreground" />
            <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Test-E-Mail senden</h2>
          </div>
          <form onSubmit={sendTest} className="flex gap-3">
            <input data-testid="settings-test-email" type="email" value={testEmail}
              onChange={e => setTestEmail(e.target.value)} placeholder="empfaenger@beispiel.ch"
              className="flex-1 px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
            <button data-testid="send-test-email-button" type="submit" disabled={testing}
              className="px-4 py-2.5 bg-secondary font-semibold hover:bg-secondary/80 disabled:opacity-50">
              {testing ? "Senden..." : "Testen"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

// ── Backup Settings ──────────────────────────────────────────────────

// ── Branding Settings ───────────────────────────────────────────────
function BrandingSettings() {
  const { branding, reload, setBranding } = useBranding();
  const confirm = useConfirm();
  const [form, setForm] = useState({ display_name: "", primary_color: "", accent_color: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = React.useRef();

  useEffect(() => {
    if (branding) {
      setForm({
        display_name: branding.display_name || "",
        primary_color: branding.primary_color || "",
        accent_color: branding.accent_color || "",
      });
    }
  }, [branding?.display_name, branding?.primary_color, branding?.accent_color]);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await api.put("/tenants/branding/me", {
        display_name: form.display_name,
        primary_color: form.primary_color,
        accent_color: form.accent_color,
      });
      await reload();
      setMsg({ type: "success", text: "Branding gespeichert" });
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Fehler beim Speichern" });
    } finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) {
      setMsg({ type: "error", text: "Logo darf max. 500 KB gross sein" });
      return;
    }
    setUploading(true); setMsg(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/tenants/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBranding({ ...branding, logo_data_uri: r.data.logo_data_uri });
      setMsg({ type: "success", text: "Logo hochgeladen" });
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Upload fehlgeschlagen" });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!(await confirm({ title: "Logo entfernen", message: "Das Workspace-Logo wird entfernt. Das DATON-Default-Icon wird wieder angezeigt.", confirmLabel: "Entfernen" }))) return;
    await api.delete("/tenants/branding/logo");
    setBranding({ ...branding, logo_data_uri: null });
    setMsg({ type: "success", text: "Logo entfernt" });
  };

  return (
    <div className="space-y-6">
      {msg && (
        <div className={`px-3 py-2 text-sm border ${msg.type === "success" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400" : "bg-destructive/10 border-destructive/20 text-destructive"}`}>
          {msg.text}
        </div>
      )}

      <div className="bg-card border border-border p-5">
        <h2 className="text-base font-bold mb-1 flex items-center gap-2"><ImageIcon size={15} /> Logo</h2>
        <p className="text-xs text-muted-foreground mb-4">Erscheint in der Sidebar und auf der Login-Seite. PNG / JPG / SVG / WEBP, max. 500 KB.</p>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 bg-muted flex items-center justify-center border border-border" data-testid="branding-logo-preview">
            {branding?.logo_data_uri ? (
              <img src={branding.logo_data_uri} alt="Logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <ImageIcon size={24} className="text-muted-foreground/50" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={uploadLogo} className="hidden" data-testid="branding-logo-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="branding-logo-upload"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50">
              <Upload size={12} /> {uploading ? "Lädt..." : branding?.logo_data_uri ? "Logo ersetzen" : "Logo hochladen"}
            </button>
            {branding?.logo_data_uri && (
              <button type="button" onClick={removeLogo} data-testid="branding-logo-remove"
                className="flex items-center gap-1.5 px-3 py-1.5 border border-border hover:border-destructive hover:text-destructive text-xs font-semibold">
                <Trash size={12} /> Entfernen
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-card border border-border p-5">
        <h2 className="text-base font-bold mb-1 flex items-center gap-2"><Palette size={15} /> Anzeigename & Farben</h2>
        <p className="text-xs text-muted-foreground mb-4">Überschreibt „DATON" in der Sidebar und nutzt deine Markenfarbe für Buttons und Akzente.</p>

        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Anzeigename</label>
            <input data-testid="branding-display-name" value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })}
              placeholder="z. B. RM Informatik" maxLength={80}
              className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <ColorField label="Primärfarbe" testId="branding-primary-color" value={form.primary_color} onChange={v => setForm({ ...form, primary_color: v })} placeholder="#e57a4d" />
            <ColorField label="Akzentfarbe" testId="branding-accent-color" value={form.accent_color} onChange={v => setForm({ ...form, accent_color: v })} placeholder="#fef0e5" />
          </div>
          <p className="text-[11px] text-muted-foreground">Leer lassen = Standardfarben (DATON Coral). Format: Hex #RRGGBB.</p>

          <div className="flex gap-2 pt-2 border-t border-border">
            <button onClick={save} disabled={saving} data-testid="branding-save"
              className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
              <Save size={14} /> {saving ? "Speichern..." : "Speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#e57a4d"} onChange={e => onChange(e.target.value)} data-testid={`${testId}-picker`}
          className="w-10 h-10 border border-border rounded cursor-pointer" />
        <input data-testid={testId} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} maxLength={7}
          className="flex-1 px-3 py-2.5 text-sm font-mono bg-background border border-input focus:border-primary focus:outline-none" />
      </div>
    </div>
  );
}


// ── Customer Numbering Settings ─────────────────────────────────────
function NumberingSettings() {
  const [cfg, setCfg] = useState({ format: "KD-{NNNN}", start: 1, preview_next: null });
  const [form, setForm] = useState({ format: "KD-{NNNN}", start: 1 });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [livePreview, setLivePreview] = useState("");

  const load = async () => {
    const r = await api.get("/customers/settings/numbering");
    setCfg(r.data);
    setForm({ format: r.data.format, start: r.data.start });
    setLivePreview(r.data.preview_next || "");
  };
  useEffect(() => { load(); }, []);

  // Client-side live preview when user edits format
  useEffect(() => {
    try {
      const now = new Date();
      let tmp = form.format
        .replace("{YYYY}", String(now.getFullYear()))
        .replace("{YY}", String(now.getFullYear() % 100).padStart(2, "0"))
        .replace("{MM}", String(now.getMonth() + 1).padStart(2, "0"));
      const m = tmp.match(/\{(N{3,6})\}/);
      const width = m ? m[1].length : 4;
      const num = String(form.start).padStart(width, "0");
      setLivePreview(m ? tmp.replace(m[0], num) : tmp + num);
    } catch { /* ignore */ }
  }, [form.format, form.start]);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await api.put("/customers/settings/numbering", form);
      setMsg({ type: "success", text: "Kundennummer-Format gespeichert" });
      load();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Fehler beim Speichern" });
    } finally { setSaving(false); }
  };

  const applyTemplate = (tpl) => setForm({ ...form, format: tpl });

  return (
    <div className="space-y-6">
      {msg && (
        <div className={`px-3 py-2 text-sm border ${msg.type === "success" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400" : "bg-destructive/10 border-destructive/20 text-destructive"}`}>
          {msg.text}
        </div>
      )}

      <div className="bg-card border border-border p-5">
        <h2 className="text-base font-bold mb-1 flex items-center gap-2"><Hash size={15} /> Kundennummern-Format</h2>
        <p className="text-xs text-muted-foreground mb-4">
          Passe das Format an deinen bestehenden Nummernkreis an. Platzhalter: <code className="text-foreground">{"{YYYY}"}</code>, <code className="text-foreground">{"{YY}"}</code>, <code className="text-foreground">{"{MM}"}</code>, <code className="text-foreground">{"{NNN}"}</code>, <code className="text-foreground">{"{NNNN}"}</code>, <code className="text-foreground">{"{NNNNN}"}</code>.
        </p>

        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Format</label>
            <input data-testid="numbering-format" value={form.format} onChange={e => setForm({ ...form, format: e.target.value })} maxLength={40}
              placeholder="KD-{NNNN}"
              className="w-full px-3 py-2.5 text-sm font-mono bg-background border border-input focus:border-primary focus:outline-none" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Start-Nummer</label>
            <input data-testid="numbering-start" type="number" min="1" value={form.start} onChange={e => setForm({ ...form, start: parseInt(e.target.value) || 1 })}
              className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
            <p className="text-[11px] text-muted-foreground mt-1">Neue Nummern starten ab dieser Zahl (wird nur beim ersten Kunden mit neuem Format angewendet).</p>
          </div>

          <div className="bg-muted/30 border border-border px-4 py-3">
            <p className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-1 flex items-center gap-1"><Eye size={11} /> Vorschau nächste Kundennummer</p>
            <p className="text-xl font-bold font-mono tracking-tight" data-testid="numbering-preview">{livePreview || "—"}</p>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Vorlagen</p>
            <div className="flex flex-wrap gap-2">
              {[
                ["KD-{NNNN}",       "KD-0001"],
                ["K{NNNNN}",        "K00001"],
                ["KND-{YYYY}-{NNN}","KND-2026-001"],
                ["{YY}{MM}-{NNN}",  "2602-001"],
                ["C-{NNNN}",        "C-0001"],
              ].map(([tpl, ex]) => (
                <button key={tpl} type="button" onClick={() => applyTemplate(tpl)} data-testid={`numbering-template-${tpl}`}
                  className="px-3 py-1.5 border border-border hover:border-primary hover:bg-primary/5 text-xs font-mono transition-colors">
                  {ex}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2 border-t border-border">
            <button onClick={save} disabled={saving} data-testid="numbering-save"
              className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
              <Save size={14} /> {saving ? "Speichern..." : "Format speichern"}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-xs text-amber-800 dark:text-amber-300">
        <strong>Hinweis:</strong> Das Format gilt nur für <strong>neue</strong> Kunden. Bestehende Kundennummern bleiben unverändert. Der Nummern-Zähler wird pro Format-Präfix separat geführt, damit Format-Wechsel keine Kollisionen erzeugen.
      </div>
    </div>
  );
}


const WEEKDAYS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
const API = process.env.REACT_APP_BACKEND_URL + "/api";

function formatBytes(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
}

function BackupSettings() {
  const confirm = useConfirm();
  const [config, setConfig] = useState({ enabled: false, schedule: "daily", hour: 2, minute: 0, weekday: 0, keep_count: 7 });
  const [backups, setBackups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [msg, setMsg] = useState(null);
  const uploadRef = React.useRef();

  const load = async () => {
    const [c, b] = await Promise.all([
      api.get("/backups/config"),
      api.get("/backups"),
    ]);
    setConfig(c.data);
    setBackups(b.data);
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      await api.put("/backups/config", config);
      setMsg({ type: "success", text: "Backup-Konfiguration gespeichert" });
      load();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Fehler beim Speichern" });
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true); setMsg(null);
    try {
      await api.post("/backups/run");
      setMsg({ type: "success", text: "Backup erstellt" });
      load();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Backup fehlgeschlagen" });
    } finally { setRunning(false); }
  };

  const restoreExisting = async (id, name) => {
    if (!(await confirm({
      title: "Backup wiederherstellen",
      message: `ACHTUNG: Wiederherstellung von "${name}" überschreibt ALLE aktuellen Daten. Alle Benutzer werden ausgeloggt.`,
      confirmLabel: "Wiederherstellen",
      variant: "warning",
      requireText: "WIEDERHERSTELLEN",
    }))) return;
    setRestoring(true); setMsg(null);
    try {
      const r = await api.post(`/backups/${id}/restore`);
      setMsg({ type: "success", text: `Wiederhergestellt: ${r.data.collections} Sammlungen, ${r.data.documents} Datensätze, ${r.data.files} Dateien. Bitte neu anmelden.` });
      setTimeout(() => window.location.href = "/login", 3000);
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Wiederherstellung fehlgeschlagen" });
    } finally { setRestoring(false); }
  };

  const restoreUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!(await confirm({
      title: "Backup aus Datei wiederherstellen",
      message: `ACHTUNG: Wiederherstellung aus "${f.name}" überschreibt ALLE aktuellen Daten. Alle Benutzer werden ausgeloggt.`,
      confirmLabel: "Wiederherstellen",
      variant: "warning",
      requireText: "WIEDERHERSTELLEN",
    }))) {
      if (uploadRef.current) uploadRef.current.value = "";
      return;
    }
    setRestoring(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/backups/restore/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg({ type: "success", text: `Wiederhergestellt: ${r.data.collections} Sammlungen, ${r.data.documents} Datensätze, ${r.data.files} Dateien. Bitte neu anmelden.` });
      setTimeout(() => window.location.href = "/login", 3000);
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Wiederherstellung fehlgeschlagen" });
    } finally {
      setRestoring(false);
      if (uploadRef.current) uploadRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Backup löschen", message: "Dieses Backup wird dauerhaft entfernt.", confirmLabel: "Löschen" }))) return;
    await api.delete(`/backups/${id}`);
    load();
  };

  const download = (id) => window.open(`${API}/backups/${id}/download`, "_blank");

  return (
    <div className="space-y-4">
      {/* Config */}
      <form onSubmit={save} className="bg-card border border-border p-6">
        <div className="flex items-center gap-2 mb-5">
          <Database size={16} className="text-muted-foreground" />
          <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Automatische Backups</h2>
        </div>

        <label className="flex items-center gap-3 mb-5 cursor-pointer">
          <div data-testid="backup-toggle"
            className={`w-11 h-6 relative transition-colors ${config.enabled ? "bg-primary" : "bg-border"} rounded-full`}
            onClick={() => setConfig(c => ({ ...c, enabled: !c.enabled }))}>
            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${config.enabled ? "translate-x-5" : ""}`} />
          </div>
          <div>
            <p className="text-sm font-semibold">Automatische Backups aktivieren</p>
            <p className="text-xs text-muted-foreground">Sichert MongoDB + Datei-Anhänge als ZIP</p>
          </div>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5 flex items-center gap-1.5"><Clock size={11} /> Häufigkeit</label>
            <select data-testid="backup-schedule" value={config.schedule}
              onChange={e => setConfig(c => ({ ...c, schedule: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none">
              <option value="daily">Täglich</option>
              <option value="weekly">Wöchentlich</option>
            </select>
          </div>

          {config.schedule === "weekly" && (
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Wochentag</label>
              <select data-testid="backup-weekday" value={config.weekday}
                onChange={e => setConfig(c => ({ ...c, weekday: parseInt(e.target.value, 10) }))}
                className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none">
                {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Uhrzeit (UTC)</label>
            <div className="flex gap-2">
              <input data-testid="backup-hour" type="number" min="0" max="23" value={config.hour}
                onChange={e => setConfig(c => ({ ...c, hour: parseInt(e.target.value || "0", 10) }))}
                className="w-20 px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
              <span className="self-center">:</span>
              <input data-testid="backup-minute" type="number" min="0" max="59" value={config.minute}
                onChange={e => setConfig(c => ({ ...c, minute: parseInt(e.target.value || "0", 10) }))}
                className="w-20 px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Server-Zeit (UTC). Wähle z.B. 02:00 für nächtliches Backup.</p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Backups behalten</label>
            <input data-testid="backup-keep" type="number" min="1" max="365" value={config.keep_count}
              onChange={e => setConfig(c => ({ ...c, keep_count: parseInt(e.target.value || "7", 10) }))}
              className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
            <p className="text-xs text-muted-foreground mt-1">Älteste werden automatisch gelöscht</p>
          </div>
        </div>

        {msg && (
          <div className={`flex items-center gap-2 px-4 py-3 mt-4 text-sm border ${msg.type === "success" ? "border-green-300 bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400 dark:border-green-900" : "border-destructive/30 bg-destructive/10 text-destructive"}`}>
            {msg.type === "success" ? <CheckCircle size={15} /> : <AlertTriangle size={15} />}{msg.text}
          </div>
        )}

        <div className="flex gap-2 mt-5">
          <button data-testid="save-backup-config" type="submit" disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50">
            <Save size={15} /> {saving ? "Speichern..." : "Konfiguration speichern"}
          </button>
          <button type="button" onClick={runNow} disabled={running} data-testid="run-backup-now"
            className="flex items-center gap-2 px-5 py-2.5 bg-secondary font-semibold hover:bg-secondary/80 disabled:opacity-50">
            <Play size={15} /> {running ? "Läuft..." : "Jetzt ausführen"}
          </button>
        </div>
      </form>

      {/* Backup list */}
      <div className="bg-card border border-border" data-testid="backup-list">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <Archive size={14} /> Vorhandene Backups ({backups.length})
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => uploadRef.current?.click()}
              disabled={restoring}
              data-testid="restore-upload-button"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/15 text-amber-700 dark:text-amber-400 text-xs font-semibold hover:bg-amber-500/25 disabled:opacity-50"
              title="Backup-ZIP hochladen und wiederherstellen"
            >
              <Upload size={12} /> ZIP hochladen
            </button>
            <input ref={uploadRef} type="file" accept=".zip" className="hidden" onChange={restoreUpload} />
            <button onClick={load} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
              <RefreshCw size={12} /> Aktualisieren
            </button>
          </div>
        </div>
        {backups.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">Keine Backups vorhanden</div>
        ) : (
          <div className="divide-y divide-border">
            {backups.map(b => (
              <div key={b._id} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`backup-${b._id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{b.filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(b.created_at).toLocaleString("de-CH")} · {formatBytes(b.size)} · {b.file_count} Dateien · {b.triggered_by}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => restoreExisting(b._id, b.filename)}
                    disabled={restoring}
                    data-testid={`restore-${b._id}`}
                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-500/15 p-1.5 rounded-md disabled:opacity-50"
                    title="Wiederherstellen"
                  >
                    <RotateCcw size={14} />
                  </button>
                  <button onClick={() => download(b._id)} className="text-muted-foreground hover:text-primary p-1.5" title="Herunterladen">
                    <Download size={14} />
                  </button>
                  <button onClick={() => remove(b._id)} className="text-muted-foreground hover:text-destructive p-1.5" title="Löschen">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {restoring && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border p-6 max-w-md text-center">
            <RefreshCw className="animate-spin mx-auto mb-3 text-primary" size={28} />
            <p className="font-semibold">Wiederherstellung läuft...</p>
            <p className="text-xs text-muted-foreground mt-1">Bitte Browser nicht schliessen</p>
          </div>
        </div>
      )}
    </div>
  );
}
